import type { ErrorCode } from '@iculogbook/shared';

/**
 * Server error codes to words a trainee reads at 3am.
 *
 * The client never renders the server's own message — that string is for a
 * developer in a network tab. Every code gets copy here, and the map is
 * exhaustive by type, so adding a code to the shared union without adding copy
 * fails the build rather than showing someone a blank error.
 */
const COPY: Record<ErrorCode, string> = {
  VALIDATION_FAILED: 'Check the details above and try again.',
  INVALID_CREDENTIALS: 'That email and password do not match an account.',
  EMAIL_IN_USE: 'An account already exists for that email. Try signing in instead.',
  PASSWORD_TOO_SHORT: 'Passwords need to be at least 12 characters.',
  PASSWORD_TOO_COMMON: 'That password is too easy to guess. Try a few unrelated words instead.',
  UNAUTHENTICATED: 'Please sign in again.',
  RESET_TOKEN_INVALID:
    'That reset link has expired or has already been used. Request a new one below.',
  RATE_LIMITED: 'Too many attempts. Wait a few minutes and try again.',
  FORBIDDEN_ORIGIN: 'Something went wrong with that request. Reload the page and try again.',
  NOT_FOUND: 'That is not available.',
  CLOCK_SKEW: "Your device's clock looks wrong, which stops entries syncing. Check date and time.",
  ADMIN_UNLOCK_FAILED: 'That did not work.',
  FEEDBACK_SEND_FAILED: 'That could not be sent. Try again in a moment.',
  INTERNAL: 'Something went wrong at our end. Try again in a moment.',
};

/** Copy for a known code, or a safe fallback for anything unrecognised. */
export function errorCopy(code: string | null): string {
  if (code !== null && code in COPY) {
    return COPY[code as ErrorCode];
  }
  return 'Something went wrong. Check your connection and try again.';
}

/** Shown when `fetch` itself fails — no signal, server unreachable. */
export const OFFLINE_COPY = 'No connection. Check your signal and try again.';
